import React, { useEffect, useState } from "react";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import {
  getSupervisorChanges,
  linkSupervisorWorker,
  mergeSupervisorUser,
  previewSupervisorMerge,
  searchKarkhanaWorkers,
  updateSupervisor,
} from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";
import ChangeList from "../sarees/ChangeList";
import type { ChangeRow } from "../sarees/sareeEditModel";
import SearchField from "../ui/SearchField";

/**
 * Owner-only fixes to a supervisor: their name and mobile (Edit), and which worker they are paid as
 * (Map). The server checks every rule (one phone per login, one worker per login, fold-in only for
 * a login-less Karkhana user of this studio); these sheets show what it says, as it says it.
 */

export type Suggestion = {
  kind: "WORKER" | "USER";
  workerId?: string | null;
  code?: string | null;
  name?: string | null;
  phoneLast4?: string | null;
  userId?: string | null;
  foldsUser?: boolean;
  match?: "PHONE" | "NAME";
};

export type SupervisorRow = {
  id: string;
  name: string;
  phone: string;
  worker?: { id: string; code: string; name?: string | null; phoneLast4?: string | null } | null;
  mapped?: boolean;
  suggestions?: Suggestion[];
  [k: string]: any;
};

export type MergePreview = {
  canMerge: boolean;
  refusal?: string | null;
  user?: { id: string; name?: string | null; code?: string | null; phoneLast4?: string | null } | null;
  workersMoving?: { id: string; code: string; name?: string | null }[];
  retire?: boolean;
  phoneFreed?: boolean;
  keptReferences?: { artisanPortraits?: number; products?: number };
};

const FIELD_LABEL: Record<string, string> = {
  NAME: "Name",
  PHONE: "Mobile number",
  WORKER_LINKED: "Worker mapped",
  WORKER_UNLINKED: "Worker unmapped",
  USER_MERGED: "Login folded in",
};

/** Change-log rows with readable field names (ChangeList translates them). */
export const supervisorChanges = (rows: any[] | null | undefined): ChangeRow[] =>
  (rows || []).map((c) => ({ ...c, field: FIELD_LABEL[c.field] || c.field }));

/** "K_273 · Saeem" for a worker chip or a picker row. */
export const workerLabel = (w: { code?: string | null; name?: string | null } | null | undefined) =>
  w ? [w.code, w.name].filter(Boolean).join(" · ") : "";

/** One server call: the whole answer back, or a "could not reach" answer, never a throw. */
const ask = async (fn: () => Promise<any>): Promise<any> => {
  try {
    return (await fn()) || { success: false, message: "Something went wrong" };
  } catch {
    return { success: false, message: "Could not reach the server", offline: true };
  }
};

/**
 * The server's message as sent. A bare code (e.g. "WORKER_NOT_FOUND") is never shown raw: a not-found
 * gets a plain sentence, anything else the fallback.
 */
export const serverText = (res: any, t: (s: string) => string, fallback = "Something went wrong"): string => {
  if (res?.offline) return t(res.message);
  const m: string = typeof res?.message === "string" ? res.message.trim() : "";
  const codeLike = !m || m === res?.code || /^[A-Z][A-Z0-9_]+$/.test(m);
  if (!codeLike) return m;
  const code = String(res?.code || m || "");
  return code.endsWith("NOT_FOUND") ? t("Not found. Refresh and try again.") : t(fallback);
};

const failNote = (res: any, t: (s: string) => string) =>
  notifications({ title: t("Error"), message: serverText(res, t), type: "danger" });

/** What mapping or folding in will do, from the server's preview. */
export const MergePreviewBox = ({ preview, supervisor }: { preview: MergePreview; supervisor: string }) => {
  const { t } = useKT();
  if (!preview.canMerge) {
    return (
      <div className="ksv-box is-warn" role="alert">
        {preview.refusal || t("This can't be done")}
      </div>
    );
  }
  const kept = (preview.keptReferences?.artisanPortraits || 0) + (preview.keptReferences?.products || 0);
  return (
    <div className="ksv-box">
      {preview.user && preview.retire ? (
        <p>
          {t("{{user}} will be folded in: that separate login is retired and its phone is freed.", {
            user: [preview.user.name, preview.user.code ? `(${preview.user.code})` : ""].filter(Boolean).join(" "),
          })}
        </p>
      ) : null}
      {(preview.workersMoving || []).map((w) => (
        <p key={w.id}>{t("{{worker}} will be {{name}}'s worker.", { worker: workerLabel(w), name: supervisor })}</p>
      ))}
      {kept ? <p className="kw-muted">{t("Portraits and products made under the old login stay as they are.")}</p> : null}
    </div>
  );
};

const NoteField = ({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) => {
  const { t } = useKT();
  return (
    <>
      <Label className="mt-2" for={id}>
        {t("Note (optional)")}
      </Label>
      <Input id={id} value={value} maxLength={200} onChange={(e) => onChange(e.target.value)} placeholder={t("e.g. took a new number")} />
    </>
  );
};

/** Edit name and mobile; a phone held by a login-less worker can be folded in from here. */
export const SupervisorEditSheet = ({
  supervisor,
  onClose,
  onSaved,
  onRefresh,
}: {
  supervisor: SupervisorRow | null;
  onClose: () => void;
  onSaved: (row: SupervisorRow) => void;
  /** Reload the list without closing (a fold-in went through but the edit after it did not). */
  onRefresh?: () => void;
}) => {
  const { t } = useKT();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A login-less worker already has the new phone: offer to fold them in, with the server's preview.
  const [fold, setFold] = useState<{ message: string; userId: string; preview?: MergePreview | null; loading?: boolean } | null>(null);
  const [changes, setChanges] = useState<ChangeRow[] | null>(null);
  const [changesError, setChangesError] = useState(false);

  const id = supervisor?.id;
  useEffect(() => {
    if (!supervisor) return;
    setName(supervisor.name || "");
    setPhone(supervisor.phone || "");
    setNote("");
    setError(null);
    setFold(null);
    setChanges(null);
    setChangesError(false);
    let live = true;
    ask(() => getSupervisorChanges(supervisor.id)).then((res) => {
      if (!live) return;
      if (res?.success) setChanges(supervisorChanges(res.data));
      else setChangesError(true);
    });
    return () => {
      live = false;
    };
    // Reset only when another supervisor is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!supervisor) return null;
  const digits = phone.replace(/\D/g, "");
  const phoneChanged = digits !== (supervisor.phone || "").replace(/\D/g, "") && digits.length > 0;
  const nameChanged = name.trim() !== (supervisor.name || "").trim();
  const canSave = !busy && (nameChanged || phoneChanged) && name.trim().length >= 2;

  // Always what the boxes say now, so a name or note edited after the fold-in offer is not lost.
  const currentEdit = () => {
    const body: { name?: string; phone?: string; note?: string } = {};
    if (nameChanged) body.name = name.trim();
    if (phoneChanged) body.phone = phone.trim();
    if (note.trim()) body.note = note.trim();
    return body;
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    setFold(null);
    const body = currentEdit();
    const res = await ask(() => updateSupervisor(supervisor.id, body));
    setBusy(false);
    if (res?.success) {
      notifications({ title: t("Done"), message: t("{{name}} is saved", { name: res.data?.name || name.trim() }), type: "success" });
      if (res.data?.changes) setChanges(supervisorChanges(res.data.changes));
      onSaved(res.data);
      return;
    }
    if (res?.code === "PHONE_HAS_KARKHANA_USER" && res.data?.userId) {
      const offer = { message: serverText(res, t), userId: res.data.userId as string, loading: true };
      setFold(offer);
      const p = await ask(() => previewSupervisorMerge(supervisor.id, { userId: offer.userId }));
      setFold({ ...offer, loading: false, preview: p?.success ? p.data : { canMerge: false, refusal: serverText(p, t) } });
      return;
    }
    setError(serverText(res, t, "Could not save"));
  };

  // Fold the other login in (frees the phone), then send the edit again from the boxes as they are now.
  // The fold-in is done even if that save then fails: the list reloads and the save's error shows here.
  const foldAndSave = async () => {
    if (!fold) return;
    setBusy(true);
    // The new phone goes with the fold, so the server refuses before folding if it would still fail.
    const merged = await ask(() => mergeSupervisorUser(supervisor.id, fold.userId, note.trim() || undefined, phoneChanged ? phone.trim() : undefined));
    if (!merged?.success) {
      setBusy(false);
      setFold({ ...fold, preview: { canMerge: false, refusal: serverText(merged, t) } });
      return;
    }
    if (merged.data?.changes) setChanges(supervisorChanges(merged.data.changes));
    onRefresh?.();
    setFold(null);
    await save();
  };

  return (
    <Modal isOpen toggle={onClose} modalClassName="kw-modal">
      <ModalHeader toggle={onClose}>{t("Edit {{name}}", { name: supervisor.name })}</ModalHeader>
      <ModalBody>
        <Label for="ksv-edit-name">{t("Name")}</Label>
        <Input id="ksv-edit-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        <Label className="mt-2" for="ksv-edit-phone">
          {t("Mobile number (they log in with this)")}
        </Label>
        <Input
          id="ksv-edit-phone"
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            setFold(null);
          }}
          placeholder={t("10 digits")}
        />
        {phoneChanged ? (
          <p className="kw-muted mt-2" style={{ marginBottom: 0 }}>
            {t("They'll be signed out on their phones and sign in again with the same PIN. Their worker's phone changes too.")}
          </p>
        ) : null}
        <NoteField id="ksv-edit-note" value={note} onChange={setNote} />
        {error ? (
          <div className="ksv-box is-warn mt-3" role="alert">
            {error}
          </div>
        ) : null}
        {fold ? (
          <div className="mt-3">
            <div className="ksv-box is-warn" role="alert">
              {fold.message}
            </div>
            {fold.loading ? <p className="kw-muted">{t("Checking…")}</p> : null}
            {fold.preview ? <MergePreviewBox preview={fold.preview} supervisor={supervisor.name} /> : null}
          </div>
        ) : null}
        <h3 className="ksv-sub">{t("Changes")}</h3>
        {changes === null && !changesError ? <p className="kw-muted">{t("Loading…")}</p> : null}
        {changesError ? <p className="kw-muted">{t("Could not load changes")}</p> : null}
        {changes && !changes.length ? <p className="kw-muted">{t("No changes yet")}</p> : null}
        {changes ? <ChangeList changes={changes} /> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>
          {t("Cancel")}
        </Button>
        {fold?.preview?.canMerge ? (
          <Button className="ResetBtn" disabled={busy} onClick={foldAndSave}>
            {busy ? t("Saving…") : t("Fold in and use this phone")}
          </Button>
        ) : (
          <Button className="ResetBtn" disabled={!canSave} onClick={save}>
            {busy ? t("Saving…") : t("Save")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

type Pick = { kind: "WORKER"; workerId: string; label: string } | { kind: "USER"; userId: string; label: string };

/** Search Workers by code, name or phone; suggestions (same phone, same name) come first. */
export const SupervisorMapSheet = ({
  supervisor,
  onClose,
  onSaved,
}: {
  supervisor: SupervisorRow | null;
  onClose: () => void;
  onSaved: (row: SupervisorRow) => void;
}) => {
  const { t } = useKT();
  const [q, setQ] = useState("");
  const [found, setFound] = useState<{ term: string; rows: any[]; failed?: boolean } | null>(null);
  const [pick, setPick] = useState<Pick | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const id = supervisor?.id;

  useEffect(() => {
    setQ("");
    setFound(null);
    setPick(null);
    setPreview(null);
    setNote("");
  }, [id]);

  const term = q.trim();
  useEffect(() => {
    if (!term) return;
    let live = true;
    const timer = window.setTimeout(async () => {
      const res = await ask(() => searchKarkhanaWorkers({ q: term, limit: 8 }));
      if (live) setFound({ term, rows: res?.success ? res.data?.docs || [] : [], failed: !res?.success });
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [term]);

  if (!supervisor) return null;

  const choose = async (p: Pick) => {
    setPick(p);
    setPreview(null);
    setChecking(true);
    const res = await ask(() => previewSupervisorMerge(supervisor.id, p.kind === "WORKER" ? { workerId: p.workerId } : { userId: p.userId }));
    setChecking(false);
    setPreview(res?.success ? res.data : { canMerge: false, refusal: serverText(res, t) });
  };

  const confirm = async () => {
    if (!pick) return;
    setBusy(true);
    const res = await ask(() =>
      pick.kind === "WORKER"
        ? linkSupervisorWorker(supervisor.id, pick.workerId, note.trim() || undefined)
        : mergeSupervisorUser(supervisor.id, pick.userId, note.trim() || undefined)
    );
    setBusy(false);
    if (!res?.success) {
      setPreview({ canMerge: false, refusal: serverText(res, t) });
      failNote(res, t);
      return;
    }
    notifications({ title: t("Done"), message: t("{{name}} is mapped", { name: supervisor.name }), type: "success" });
    onSaved(res.data);
  };

  const suggestions = supervisor.suggestions || [];
  const searching = Boolean(term) && found?.term !== term;
  const rows = term && found?.term === term ? found.rows : [];

  return (
    <Modal isOpen toggle={onClose} modalClassName="kw-modal">
      <ModalHeader toggle={onClose}>{t("Map {{name}} to a worker", { name: supervisor.name })}</ModalHeader>
      <ModalBody>
        {pick ? (
          <>
            <p style={{ marginTop: 0 }}>
              <strong>{pick.label}</strong>
            </p>
            {checking ? <p className="kw-muted">{t("Checking…")}</p> : null}
            {preview ? <MergePreviewBox preview={preview} supervisor={supervisor.name} /> : null}
            {preview?.canMerge ? <NoteField id="ksv-map-note" value={note} onChange={setNote} /> : null}
          </>
        ) : (
          <>
            {suggestions.length ? (
              <>
                <h3 className="ksv-sub" style={{ marginTop: 0 }}>
                  {t("Looks like the same person")}
                </h3>
                <ul className="ksv-pick">
                  {suggestions.map((s, i) => {
                    // Only a suggestion the server gave an id for can be picked.
                    const target: Pick | null =
                      s.kind === "WORKER" && s.workerId
                        ? { kind: "WORKER", workerId: s.workerId, label: workerLabel(s) }
                        : s.kind === "USER" && s.userId
                        ? { kind: "USER", userId: s.userId, label: workerLabel(s) }
                        : null;
                    return (
                    <li key={`${s.kind}-${s.workerId || s.userId || i}`}>
                      <button type="button" className="kw-press" disabled={!target} onClick={() => target && choose(target)}>
                        <strong>
                          {s.code ? <span className="kw-code">{s.code}</span> : null} {s.name}
                        </strong>
                        <span className="kw-muted">
                          {[
                            s.match === "PHONE" ? t("Same phone") : t("Same name"),
                            s.phoneLast4 ? `…${s.phoneLast4}` : "",
                            s.kind === "USER" ? t("Karkhana user, no worker") : s.foldsUser ? t("Has a login to fold in") : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </button>
                    </li>
                    );
                  })}
                </ul>
              </>
            ) : null}
            <SearchField value={q} onChange={setQ} placeholder={t("Code, name or phone, e.g. K_273")} label={t("Find a worker")} autoCapitalize="characters" />
            {!term ? <p className="kw-muted">{t("Type a code, name or phone to find their worker.")}</p> : null}
            {searching ? <p className="kw-muted">{t("Looking in Workers…")}</p> : null}
            {term && !searching && found?.failed ? <p className="kw-muted">{t("Could not search Workers. Try again.")}</p> : null}
            {term && !searching && !found?.failed && !rows.length ? (
              <p className="kw-muted">{t("No one called “{{name}}” in Workers.", { name: term })}</p>
            ) : null}
            {rows.length ? (
              <ul className="ksv-pick">
                {rows.map((w) => {
                  const linkedTo = w.userId && w.userName && w.userId !== supervisor.id ? w.userName : "";
                  return (
                    <li key={w.id}>
                      <button
                        type="button"
                        className="kw-press"
                        onClick={() => choose({ kind: "WORKER", workerId: w.id, label: workerLabel({ code: w.workerCode, name: w.name || w.userName }) })}
                      >
                        <strong>
                          <span className="kw-code">{w.workerCode}</span> {w.name || w.userName}
                        </strong>
                        <span className="kw-muted">
                          {[w.phone ? `…${String(w.phone).slice(-4)}` : "", linkedTo ? t("Login: {{name}}", { name: linkedTo }) : ""]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {pick ? (
          <Button
            color="secondary"
            outline
            onClick={() => {
              setPick(null);
              setPreview(null);
            }}
          >
            {t("Back")}
          </Button>
        ) : (
          <Button color="secondary" outline onClick={onClose}>
            {t("Cancel")}
          </Button>
        )}
        {pick ? (
          <Button className="ResetBtn" disabled={busy || !preview?.canMerge} onClick={confirm}>
            {busy ? t("Saving…") : preview?.user && preview.retire ? t("Fold in and map") : t("Map to worker")}
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};
